// MP738 — standalone-app mode by hostname: the pure rules.
//
// One code tree, several front doors (ADR 0029, ADR 0030 decision 2, the
// Resilience scoping doc). On a standalone product's own hostname the
// same React tree renders that one product: no app switcher, no launcher,
// no cross-app menus, its own name on the sign-in page. A standalone
// tenant is an ordinary X-One tenant entitled to that app.
//
// This module holds everything that decides the mode and can be tested
// without a browser: the per-app registry, the host-map parser, the
// hostname rule and the route guard rule. No import.meta, no window — it
// runs under plain tsx (`cd apps/web && npx tsx src/lib/standaloneApp.test.ts`).
// lib/tenantHost.ts reads the environment and calls into here; every
// consumer (AppLayout, LauncherPage, LoginPage, SignupPage, the route
// guard in App.tsx) reads that ONE function, so standalone mode never
// becomes a second code path.
//
// Adding a product ('surface', 'exposure' …) is ONE entry in
// STANDALONE_REGISTRY below (plus its suite app in lib/apps.ts, which it
// borrows its name, icon and routes from), and its id in shared-types
// STANDALONE_APP_IDS, the server-side org flag (MP486). The test checks
// the two lists agree.

import { APPS, TENANT_SIDEBAR, getApp, resolveAppFromPathname, type AppKey, type AppSidebarItem } from './apps';

/** Copy and marks a standalone product needs beyond its suite entry. */
interface StandaloneSpec {
  /** The suite app this standalone product is (lib/apps.ts). Its name,
   *  icon, root path and entry route are reused, never repeated here. */
  appKey: AppKey;
  /** One or two letters for the browser-tab icon. */
  monogram: string;
  /** Sign-in card description. */
  signInDescription: string;
}

/**
 * The standalone products. The key is the value used in
 * `VITE_STANDALONE_HOST_MAP` and `VITE_DEV_STANDALONE_APP`.
 */
export const STANDALONE_REGISTRY = {
  resilience: {
    appKey: 'resilience',
    monogram: 'R',
    signInDescription: 'Sign in to protect, test and recover your critical systems.',
  },
  'ai-gateway': {
    appKey: 'ai-gateway',
    monogram: 'G',
    signInDescription: 'Sign in to route, govern and observe your AI traffic.',
  },
  // MP486 — Umbra Standalone: an ordinary X-One org flagged
  // `standaloneApp: 'umbra'` (entitled to app.umbra only, server side),
  // served on the host mapped to `umbra` in VITE_STANDALONE_HOST_MAP.
  umbra: {
    appKey: 'umbra',
    monogram: 'U',
    signInDescription: 'Sign in to keep your management interfaces and remote access hidden from the internet.',
  },
} as const satisfies Record<string, StandaloneSpec>;

export type StandaloneApp = keyof typeof STANDALONE_REGISTRY;

export const STANDALONE_APP_IDS = Object.keys(STANDALONE_REGISTRY) as StandaloneApp[];

export function isStandaloneApp(value: unknown): value is StandaloneApp {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(STANDALONE_REGISTRY, value);
}

/** Everything the chrome needs to render one standalone product. */
export interface StandaloneBrand {
  app: StandaloneApp;
  appKey: AppKey;
  /** "X-One Resilience" — header, sign-in wordmark, tab title. */
  productName: string;
  /** "Resilience" — the menu row that takes you back to the product. */
  shortName: string;
  /** Icon key in AppLayout's registry (the app's own icon). */
  iconKey: string;
  rootPath: string;
  /** Where the launcher, `/` and every other app's routes go instead. */
  entryRoute: string;
  monogram: string;
  signInDescription: string;
}

export function standaloneBrand(app: StandaloneApp): StandaloneBrand {
  const spec: StandaloneSpec = STANDALONE_REGISTRY[app];
  const def = getApp(spec.appKey);
  return {
    app,
    appKey: def.key,
    productName: def.name,
    shortName: def.shortName,
    iconKey: def.iconKey,
    rootPath: def.rootPath,
    entryRoute: def.entryRoute ?? def.rootPath,
    monogram: spec.monogram,
    signInDescription: spec.signInDescription,
  };
}

// ── Hostname → standalone app ─────────────────────────────────────────

export interface StandaloneHostHint {
  app: StandaloneApp | null;
  source: 'hostname' | 'env-override' | 'none';
  /** Configuration problems found on the way (ignored entries, a bad
   *  override). The hint is still safe; these are for a console warning. */
  issues?: string[];
}

const NONE: StandaloneHostHint = { app: null, source: 'none' };

// RFC 1123 labels, lower-case, at least two labels, no port/scheme/path.
const LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
function isHostname(h: string): boolean {
  if (h.length > 253) return false;
  const labels = h.split('.');
  return labels.length >= 2 && labels.every((l) => LABEL_RE.test(l));
}

function normalizeHost(h: string): string {
  return h.trim().toLowerCase().replace(/\.$/, '');
}

/** Hosts that only ever mean "this machine": the env override applies here. */
export function isLocalHost(hostname: string): boolean {
  const h = normalizeHost(hostname);
  return (
    h === 'localhost' ||
    h.endsWith('.localhost') ||
    h.endsWith('.local') ||
    h === '127.0.0.1' ||
    h === '[::1]' ||
    h === '::1'
  );
}

export interface ParsedHostMap {
  map: ReadonlyMap<string, StandaloneApp>;
  issues: string[];
}

/**
 * Parse `VITE_STANDALONE_HOST_MAP`:
 *   `resilience.example.com=resilience,gateway.example.com=ai-gateway`
 * Entries are separated by commas, semicolons or whitespace. Strict:
 * an entry that is not `<hostname>=<known app>` is ignored and reported;
 * a host listed for two different apps is ambiguous and dropped
 * entirely; the suite's own host can never be standalone. Anything
 * dropped falls back to the suite chrome, which is always safe.
 */
export function parseStandaloneHostMap(raw: string | null | undefined, appHost?: string): ParsedHostMap {
  const issues: string[] = [];
  const map = new Map<string, StandaloneApp>();
  const ambiguous = new Set<string>();
  const suiteHost = appHost ? normalizeHost(appHost) : '';
  const entries = (raw ?? '').split(/[\s,;]+/).filter(Boolean);
  for (const entry of entries) {
    const eq = entry.indexOf('=');
    if (eq <= 0 || eq !== entry.lastIndexOf('=')) {
      issues.push(`"${entry}" is not <hostname>=<app>`);
      continue;
    }
    const host = normalizeHost(entry.slice(0, eq));
    const app = entry.slice(eq + 1).trim().toLowerCase();
    if (!isHostname(host)) {
      issues.push(`"${entry}": "${host}" is not a bare hostname (no scheme, port or path)`);
      continue;
    }
    if (!isStandaloneApp(app)) {
      issues.push(`"${entry}": unknown app "${app}" (expected one of ${STANDALONE_APP_IDS.join(', ')})`);
      continue;
    }
    if (suiteHost && host === suiteHost) {
      issues.push(`"${entry}": ${host} is the suite's own host and cannot be a standalone host`);
      continue;
    }
    if (ambiguous.has(host)) continue;
    const prior = map.get(host);
    if (prior && prior !== app) {
      issues.push(`${host} is mapped to both "${prior}" and "${app}"; ignoring it`);
      map.delete(host);
      ambiguous.add(host);
      continue;
    }
    map.set(host, app);
  }
  return { map, issues };
}

/**
 * The rule, mirroring tenantHostRules.deriveTenantHint:
 *  1. `VITE_DEV_STANDALONE_APP`, honoured on a local host only, so a
 *     stray build variable can never turn a deployed suite standalone;
 *  2. an exact hostname match in the host map;
 *  3. otherwise none — the suite chrome.
 */
export function deriveStandaloneHint(
  hostname: string,
  rawHostMap: string | null | undefined,
  appHost: string,
  envOverride?: string | null,
): StandaloneHostHint {
  const host = normalizeHost(hostname ?? '');
  const issues: string[] = [];
  const override = (envOverride ?? '').trim().toLowerCase();
  if (override) {
    if (!isLocalHost(host)) {
      issues.push(`VITE_DEV_STANDALONE_APP is set but ignored on ${host || 'an unknown host'} (local hosts only)`);
    } else if (isStandaloneApp(override)) {
      return { app: override, source: 'env-override' };
    } else {
      issues.push(`VITE_DEV_STANDALONE_APP="${override}" is not one of ${STANDALONE_APP_IDS.join(', ')}`);
    }
  }
  const parsed = parseStandaloneHostMap(rawHostMap, appHost);
  issues.push(...parsed.issues);
  const app = host ? parsed.map.get(host) ?? null : null;
  const hint: StandaloneHostHint = app ? { app, source: 'hostname' } : { ...NONE };
  return issues.length ? { ...hint, issues } : hint;
}

// ── Route guard ───────────────────────────────────────────────────────

/** Organization-wide menu sections that span every app (Policies, the
 *  Insights group: dashboards, member risk, AI Insights, reports, KPIs).
 *  Hidden from the standalone menu, and their routes redirect. */
export const STANDALONE_HIDDEN_TENANT_SECTIONS = ['insights'] as const satisfies ReadonlyArray<keyof typeof TENANT_SIDEBAR>;

/** Suite-only pages outside the app registry: the launcher, the Aware
 *  learner portal, and the suite's marketing pages. */
const SUITE_ONLY_EXTRA_ROOTS = ['/launcher', '/learn', '/home', '/pricing', '/how-it-works'];

function rootsOf(items: readonly AppSidebarItem[], out: Set<string>): Set<string> {
  for (const it of items) {
    const path = it.to.split('#')[0];
    if (path.startsWith('/')) out.add(path);
    if (it.children) rootsOf(it.children, out);
  }
  return out;
}

/** Every path root that belongs to the suite rather than one product. */
export const SUITE_ONLY_ROOTS: readonly string[] = (() => {
  const roots = new Set<string>(SUITE_ONLY_EXTRA_ROOTS);
  for (const key of STANDALONE_HIDDEN_TENANT_SECTIONS) rootsOf(TENANT_SIDEBAR[key].items, roots);
  return [...roots];
})();

function under(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(root + '/');
}

/**
 * Where a standalone host sends this path, or null to render it.
 *  - another app's routes (by the registry's rootPath) → the entry route;
 *  - the launcher and the cross-app organization pages → the entry route;
 *  - everything else (the product's own routes, /settings, /identity/*,
 *    /integrations/*, /audit, /shell/profile, sign-in, legal…) renders.
 */
export function standaloneRedirectTarget(pathname: string, app: StandaloneApp | null): string | null {
  if (!app) return null;
  const brand = standaloneBrand(app);
  const owner = resolveAppFromPathname(pathname);
  if (owner && owner.key !== brand.appKey) return brand.entryRoute;
  if (SUITE_ONLY_ROOTS.some((r) => under(pathname, r))) return brand.entryRoute;
  return null;
}

/** Where a signed-in person lands (`/`, after sign-in): the launcher on
 *  the suite, the product's entry route on a standalone host. */
export function signedInHome(app: StandaloneApp | null): string {
  return app ? standaloneBrand(app).entryRoute : '/launcher';
}

/** Apps the chrome may list on this host: just the product when
 *  standalone, otherwise the caller's list unchanged. */
export function appsForHost<T extends { key: AppKey }>(apps: readonly T[], app: StandaloneApp | null): T[] {
  if (!app) return [...apps];
  const key = STANDALONE_REGISTRY[app].appKey;
  return apps.filter((a) => a.key === key);
}

/** Browser-tab icon: the product's monogram on the indigo brand tile. */
export function standaloneFaviconDataUri(brand: StandaloneBrand): string {
  const letter = brand.monogram.replace(/[^A-Za-z0-9]/g, '').slice(0, 2) || '?';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">` +
    `<rect width="32" height="32" rx="7" fill="#4f46e5"/>` +
    `<text x="16" y="22" text-anchor="middle" font-family="system-ui,-apple-system,Segoe UI,sans-serif" font-size="${letter.length > 1 ? 14 : 18}" font-weight="700" fill="#ffffff">${letter}</text>` +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// Kept so a registry entry can never point at an app that is missing or
// lives on another host (checked by standaloneApp.test.ts too).
export function registryProblems(): string[] {
  const problems: string[] = [];
  for (const id of STANDALONE_APP_IDS) {
    const def = APPS.find((a) => a.key === STANDALONE_REGISTRY[id].appKey);
    if (!def) problems.push(`${id}: no suite app "${STANDALONE_REGISTRY[id].appKey}"`);
    else if (def.external || !def.entryRoute) problems.push(`${id}: ${def.key} has no in-app entry route`);
  }
  return problems;
}
